import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core'
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms'
import { SeFeMenuComponent, SeFeMenuOptions } from 'se-fe-menu'
import { SeFeTranslationsLoader } from 'se-fe-translations'

export interface OpenRegistrationFilterOption {
  id: string
  name: string
}

export interface OpenRegistrationFilters {
  sport: string[]
  type: string[]
  organization: string[]
}

type OpenRegistrationFilterKey = keyof OpenRegistrationFilters
type CheckboxOptionsById = Record<string, { label: string; value: boolean }[]>

@Component({
  selector: 'se-po-open-registration-filter',
  templateUrl: './open-registration-filter.component.html',
  styleUrls: ['./open-registration-filter.component.scss'],
  standalone: false,
})
export class OpenRegistrationFilterComponent implements OnChanges, OnInit {
  public readonly translationKey =
    'SE_PO_SE_REGISTRATION_UI_FEATURE_OPEN_REGISTRATION_LIST.'
  private readonly translationsPath =
    'se-registration-ui/feature-open-registration-list/assets/translations'

  @Input() public sports: OpenRegistrationFilterOption[] = []
  @Input() public signupTypeList: OpenRegistrationFilterOption[] = []
  @Input() public organizations: OpenRegistrationFilterOption[] = []

  @Output() public filtersChange = new EventEmitter<OpenRegistrationFilters>()

  public readonly filtersForm = new UntypedFormGroup({
    sport: new UntypedFormGroup({}),
    organization: new UntypedFormGroup({}),
    type: new UntypedFormGroup({}),
  })
  public readonly sportMenuOptions: SeFeMenuOptions = {
    name: 'sportMenu',
    title: 'Sport',
    maxHeight: '50vh',
    minWidth: '280px',
  }
  public readonly organizationMenuOptions: SeFeMenuOptions = {
    name: 'organizationMenu',
    title: 'Organization',
    maxHeight: '50vh',
    minWidth: '280px',
  }
  public readonly typeMenuOptions: SeFeMenuOptions = {
    name: 'typeMenu',
    title: 'Type',
    maxHeight: '50vh',
    minWidth: '280px',
  }

  public selectedSportIds: string[] = []
  public selectedOrganizationIds: string[] = []
  public selectedTypeIds: string[] = []
  public sportCheckboxOptions: CheckboxOptionsById = {}
  public organizationCheckboxOptions: CheckboxOptionsById = {}
  public typeCheckboxOptions: CheckboxOptionsById = {}

  public get sportForm(): UntypedFormGroup {
    return this.filtersForm.get('sport') as UntypedFormGroup
  }

  public get organizationForm(): UntypedFormGroup {
    return this.filtersForm.get('organization') as UntypedFormGroup
  }

  public get sportFilterDisabled(): boolean {
    return this.sports.length === 0
  }

  public get organizationFilterDisabled(): boolean {
    return this.organizations.length === 0
  }

  public get typeFilterDisabled(): boolean {
    return this.signupTypeList.length === 0
  }

  public get typeForm(): UntypedFormGroup {
    return this.filtersForm.get('type') as UntypedFormGroup
  }

  public get sportChipText(): string {
    return this.checkboxChipText(
      this.translationKey + 'FILTER.sport',
      this.sports,
      this.selectedSportIds
    )
  }

  public get typeChipText(): string {
    return this.checkboxChipText(
      this.translationKey + 'FILTER.type',
      this.signupTypeList,
      this.selectedTypeIds
    )
  }

  public get organizationChipText(): string {
    return this.checkboxChipText(
      this.translationKey + 'FILTER.organization',
      this.organizations,
      this.selectedOrganizationIds
    )
  }

  constructor(private translationsLoader: SeFeTranslationsLoader) {}

  public ngOnInit(): void {
    void this.translationsLoader.load(this.translationsPath)
  }

  public ngOnChanges(): void {
    this.syncSelectedValuesWithOptions()
  }

  public hasSelectedValues(selectedIds: string[]): boolean {
    return selectedIds.length > 0
  }

  public applyFilter(filterKey: OpenRegistrationFilterKey, menu?: SeFeMenuComponent): void {
    const nextSelectedIds = this.selectedIdsFromControlValues(
      this.filterForm(filterKey).value,
      this.filterOptions(filterKey)
    )

    if (this.selectedIdsChanged(this.selectedIds(filterKey), nextSelectedIds)) {
      this.setSelectedIds(filterKey, nextSelectedIds)
      this.emitFilters()
    }

    menu?.closeMenu()
  }

  public clearFilter(filterKey: OpenRegistrationFilterKey, menu?: SeFeMenuComponent): void {
    const form = this.filterForm(filterKey)
    Object.keys(form.controls).forEach((id) => {
      form.get(id).setValue(false, { emitEvent: false })
    })
    this.setSelectedIds(filterKey, [])
    this.emitFilters()
    menu?.closeMenu()
  }

  private syncSelectedValuesWithOptions(): void {
    const previousSportIds = this.selectedSportIds
    const previousOrganizationIds = this.selectedOrganizationIds
    const previousTypeIds = this.selectedTypeIds

    this.selectedSportIds = this.keepExistingSelections(this.selectedSportIds, this.sports)
    this.syncCheckboxControls(this.sportForm, this.sports, this.selectedSportIds)
    this.sportCheckboxOptions = this.toCheckboxOptions(this.sports)

    this.selectedOrganizationIds = this.keepExistingSelections(
      this.selectedOrganizationIds,
      this.organizations
    )
    this.syncCheckboxControls(this.organizationForm, this.organizations, this.selectedOrganizationIds)
    this.organizationCheckboxOptions = this.toCheckboxOptions(this.organizations)

    this.selectedTypeIds = this.keepExistingSelections(this.selectedTypeIds, this.signupTypeList)
    this.syncCheckboxControls(this.typeForm, this.signupTypeList, this.selectedTypeIds)
    this.typeCheckboxOptions = this.toCheckboxOptions(this.signupTypeList)

    if (this.selectionChanged(previousSportIds, previousOrganizationIds, previousTypeIds)) {
      this.emitFilters()
    }
  }

  private keepExistingSelections(selectedIds: string[], options: OpenRegistrationFilterOption[]): string[] {
    const optionIds = new Set(options.map(({ id }) => id))
    return selectedIds.filter((id) => optionIds.has(id))
  }

  private syncCheckboxControls(
    group: UntypedFormGroup,
    options: OpenRegistrationFilterOption[],
    selectedIds: string[]
  ): void {
    const optionIds = new Set(options.map(({ id }) => id))
    Object.keys(group.controls).forEach((id) => {
      if (!optionIds.has(id)) group.removeControl(id, { emitEvent: false })
    })

    options.forEach(({ id }) => {
      const selected = selectedIds.includes(id)
      if (group.contains(id)) {
        group.get(id).setValue(selected, { emitEvent: false })
      } else {
        group.addControl(id, new UntypedFormControl(selected), { emitEvent: false })
      }
    })
  }

  private selectedIdsFromControlValues(
    values: Record<string, boolean>,
    options: OpenRegistrationFilterOption[]
  ): string[] {
    return options
      .filter(({ id }) => values?.[id])
      .map(({ id }) => id)
  }

  private toCheckboxOptions(options: OpenRegistrationFilterOption[]): CheckboxOptionsById {
    return options.reduce((checkboxOptions, { id, name }) => ({
      ...checkboxOptions,
      [id]: [{ label: name, value: true }],
    }), {})
  }

  private checkboxChipText(
    defaultText: string,
    options: OpenRegistrationFilterOption[],
    selectedIds: string[]
  ): string {
    if (selectedIds.length === 1) {
      return options.find(({ id }) => id === selectedIds[0])?.name || defaultText
    }

    return defaultText
  }

  private filterForm(filterKey: OpenRegistrationFilterKey): UntypedFormGroup {
    const forms: Record<OpenRegistrationFilterKey, UntypedFormGroup> = {
      sport: this.sportForm,
      organization: this.organizationForm,
      type: this.typeForm,
    }
    return forms[filterKey]
  }

  private filterOptions(filterKey: OpenRegistrationFilterKey): OpenRegistrationFilterOption[] {
    const options: Record<OpenRegistrationFilterKey, OpenRegistrationFilterOption[]> = {
      sport: this.sports,
      organization: this.organizations,
      type: this.signupTypeList,
    }
    return options[filterKey]
  }

  private setSelectedIds(filterKey: OpenRegistrationFilterKey, ids: string[]): void {
    if (filterKey === 'sport') this.selectedSportIds = ids
    if (filterKey === 'organization') this.selectedOrganizationIds = ids
    if (filterKey === 'type') this.selectedTypeIds = ids
  }

  private selectedIds(filterKey: OpenRegistrationFilterKey): string[] {
    const selectedIds: Record<OpenRegistrationFilterKey, string[]> = {
      sport: this.selectedSportIds,
      organization: this.selectedOrganizationIds,
      type: this.selectedTypeIds,
    }
    return selectedIds[filterKey]
  }

  private selectedIdsChanged(previousIds: string[], nextIds: string[]): boolean {
    return previousIds.length !== nextIds.length ||
      previousIds.some((id, index) => id !== nextIds[index])
  }

  private selectionChanged(
    previousSportIds: string[],
    previousOrganizationIds: string[],
    previousTypeIds: string[]
  ): boolean {
    return previousSportIds.length !== this.selectedSportIds.length ||
      previousSportIds.some((id, index) => id !== this.selectedSportIds[index]) ||
      previousOrganizationIds.length !== this.selectedOrganizationIds.length ||
      previousOrganizationIds.some((id, index) => id !== this.selectedOrganizationIds[index]) ||
      previousTypeIds.length !== this.selectedTypeIds.length ||
      previousTypeIds.some((id, index) => id !== this.selectedTypeIds[index])
  }

  private emitFilters(): void {
    this.filtersChange.emit({
      sport: [...this.selectedSportIds],
      type: [...this.selectedTypeIds],
      organization: [...this.selectedOrganizationIds],
    })
  }
}
